import * as R from 'ramda';

import { getTransitColors } from '@utils/transitColors.ts';

import type {
  MapFeature,
  MapGeoJsonSource,
  MapInitialized,
  MapInstance,
  MapTheme,
  ThemeAttributeValue,
} from './types.ts';
import { setSource } from './utils.ts';

const temporaryDefaultThemes = {
  'nav.multipoint': {
    darkFillColor: '#2DD9FF',
    fillColor: '#387AFF',
    fillColorOpacity: 0.5,
    strokeAlign: 'center',
    strokeColor: '#FFFFFF',
    strokeWidth: 1,
  },
  'nav.alternativemultipoint': {
    darkFillColor: '#737DAF',
    fillColor: '#8692B1',
    fillColorOpacity: 0.5,
    strokeAlign: 'center',
    strokeColor: '#8692B1',
    strokeWidth: 1,
  },
};

export const mergeWithProp = <T extends Record<string, unknown>>(
  prop: string,
  toAdd: Record<string, ThemeAttributeValue>,
  o: T,
): T => {
  const oldValue = o?.[prop];
  const cleanedToAdd = Object.fromEntries(Object.entries(toAdd).filter(([, val]) => val !== '')); // if the value in the theme in is an empty string, we don't want to apply it and prefer the value in the geoJson (ie feature)
  return {
    ...o,
    [prop]: {
      ...(typeof oldValue === 'object' && oldValue !== null ? oldValue : {}),
      ...cleanedToAdd,
    },
  } as T;
};

const mergeWithPropTyped = <T extends Record<string, unknown>>(
  prop: keyof T,
  toAdd: Record<string, ThemeAttributeValue>,
  o: T,
): T => mergeWithProp(prop as string, toAdd, o) as T;

const addAttributesFromTheme = (
  theme: MapTheme,
  feature: MapFeature,
  attributes: string[],
  darkAttributes: string[],
  isDarkMode: boolean,
): MapFeature => {
  const category = R.path(['properties', 'category'], feature);
  // if the feature doesn't have a category or has an empty one (because !'' === true) - don't apply the theme
  if (!category) {
    return feature;
  }
  if (!theme[category]) {
    console.warn(`Unable to find category '${category}' in theme.`);
  }

  const themeCategory = R.mergeAll([
    theme.default ?? {},
    theme.selected ?? {},
    theme.desaturation ?? {},
    theme[String(category)] ?? {},
  ]) as Record<string, ThemeAttributeValue>;

  const darkModeProperties = isDarkMode
    ? Object.fromEntries(
        attributes
          .map((attribute, index) => [attribute, themeCategory[darkAttributes[index]]])
          .filter(([, value]) => Boolean(value)),
      )
    : {};

  const updates = {
    ...R.pick(attributes, themeCategory),
    ...darkModeProperties,
  } as Record<string, ThemeAttributeValue>;
  // Merge with Prop takes the theme updates above, which prefer the values to the right side of the merge,
  // and only applies those to the feature properties if they are not empty strings.
  return mergeWithPropTyped('properties', updates, feature);
};

const baseThemeableAttributes = ['fillColor', 'fillColorOpacity', 'haloColor', 'strokeColor', 'textColor'];
const baseFeatureAttributePrefixes = ['desaturation', 'selected'];

const capitalizeFirstLetter = (s: string): string => s[0].toUpperCase() + s.slice(1);

const getThemeableAttributes = R.memoizeWith(
  () => 'themeableAttributes',
  (): string[] => {
    const addPrefixes = R.pipe(R.map(capitalizeFirstLetter), R.xprod(baseFeatureAttributePrefixes), R.map(R.join('')));
    const prefixedAttributes = addPrefixes(baseThemeableAttributes);
    return baseThemeableAttributes.concat(prefixedAttributes);
  },
);

const getDarkThemeAttributes = R.memoizeWith(
  (attributes: string[]) => attributes.join('|'),
  (attributes: string[]): string[] => attributes.map(attribute => 'dark' + capitalizeFirstLetter(attribute)),
);

const updateFeatureFromTheme = (feature: MapFeature, theme: MapTheme, isDarkMode = false): MapFeature => {
  const attributes = getThemeableAttributes();
  const darkAttributes = getDarkThemeAttributes(attributes);
  return addAttributesFromTheme(theme, feature, attributes, darkAttributes, isDarkMode);
};

export default function MapThemeController(
  app: { bus: { on: <TPayload = unknown>(eventName: string, handler: (payload: TPayload) => unknown) => void } },
  mapInitialized: MapInitialized,
) {
  let state = {
    mapTheme: null as MapTheme | null,
    isDarkThemeOn: false,
  };

  const updateMapBackgroundColor = (map: MapInstance): void => {
    const { mapTheme, isDarkThemeOn } = state;
    if (!mapTheme) {
      return;
    }
    const colorPropertyName = isDarkThemeOn ? 'darkFillColor' : 'fillColor';
    const artBoardColor = R.path(['basemap.venue', colorPropertyName], mapTheme) as string | undefined;
    if (artBoardColor?.length) {
      map.setPaintProperty('background', 'background-color', artBoardColor);
    }
  };

  const augmentTheme = (theme: MapTheme): MapTheme => {
    // By default, use the nav.primary.fillColor for the nav.multipoint.fillColor - just at 50% opacity
    if (theme['nav.primary']) {
      if (typeof theme['nav.primary'].fillColor === 'string') {
        temporaryDefaultThemes['nav.multipoint'].fillColor = theme['nav.primary'].fillColor;
      }
      if (typeof theme['nav.primary'].darkFillColor === 'string') {
        temporaryDefaultThemes['nav.multipoint'].darkFillColor = theme['nav.primary'].darkFillColor;
      }
    }
    // // By default, use the nav.alternative.fillColor for the nav.alternativemultipoint.fillColor - just at 50% opacity
    if (theme['nav.alternative']) {
      if (typeof theme['nav.alternative'].fillColor === 'string') {
        temporaryDefaultThemes['nav.alternativemultipoint'].fillColor = theme['nav.alternative'].fillColor;
      }
      if (typeof theme['nav.alternative'].darkFillColor === 'string') {
        temporaryDefaultThemes['nav.alternativemultipoint'].darkFillColor = theme['nav.alternative'].darkFillColor;
      }
    }

    const { train, bus } = getTransitColors(theme);
    return {
      ...temporaryDefaultThemes,
      ...theme,
      'nav.train': { ...theme['nav.train'], fillColor: train },
      'nav.transit': { ...theme['nav.transit'], fillColor: bus },
    };
  };

  mapInitialized().then(updateMapBackgroundColor);

  app.bus.on('venueData/mapDataLoaded', async ({ mapTheme }: { mapTheme: MapTheme }) => {
    mapTheme = augmentTheme(mapTheme);
    state = { ...state, mapTheme };
  });

  app.bus.on('map/getMapFeatureThemer', () => {
    return (feature: MapFeature): MapFeature => {
      return state.mapTheme ? updateFeatureFromTheme(feature, state.mapTheme, state.isDarkThemeOn) : feature;
    };
  });

  const updateTheming = () => {
    mapInitialized().then((map: MapInstance) => {
      if (!state.mapTheme) {
        return;
      }
      R.forEachObjIndexed(
        (source: MapGeoJsonSource, sourceId: string) => {
          const newSource = source.data.features.map((feature: MapFeature) =>
            updateFeatureFromTheme(feature, state.mapTheme as MapTheme, state.isDarkThemeOn),
          );
          setSource(sourceId, newSource, map);
        },
        map.getStyle().sources as Record<string, MapGeoJsonSource>,
      );
      updateMapBackgroundColor(map);
    });
  };

  app.bus.on('map/toggleDarkMode', async () => {
    state = { ...state, isDarkThemeOn: !state.isDarkThemeOn };
    updateTheming();
  });

  app.bus.on('map/replaceTheme', ({ theme }: { theme: MapTheme }) => {
    theme = augmentTheme(theme);
    state = { ...state, mapTheme: theme };
    updateTheming();
  });

  const runTest = async (initialState: Partial<typeof state>, testRoutine: () => Promise<void> | void) => {
    state = R.mergeRight(state, initialState);
    await testRoutine();
    return state;
  };

  return { runTest };
}
